import React, { useCallback, useEffect, useRef, useState } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterFeature,
  CharacterFeat,
  Feat,
  FeatFeature,
  CharacterSpell,
  CharacterItem,
  SummonOption2,
  PseudoItem,
} from "@dndModels";
import { ModelBase } from "@models";
import { FeatTier } from "@dndModels/Types";

import DandDAPI from "@utilities/smart_api";
import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import DataUtilities from '@dndUtilities/data_utilities';

import CharacterListOptionsInput from "@dndComponents/character/misc/CharacterListOptionsInput";
import LoadingBox from "@components/input/LoadingBox";
import CheckBox from "@components/input/CheckBox";

interface Props {
  char: Character;
  obj: CharacterFeature;
  onChange: (changed: CharacterFeature) => void;
}

const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function CharacterFeatBox({
  char,
  obj,
  onChange,
}: Props) {
  const fos = obj.feature_options as CharacterFeat[];
  const [fetched, setFetched] = useState(true);
  const [cf_id, setCFId] = useState("");
  const [use_feats, setUseFeats] = useState<Feat[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [changing, setChanging] = useState(false);
  const [flex_mode, setFlexMode] = useState(false);
  const [recalc, setRecalc] = useState(false);
  const [reloading, setReloading] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculated = useCallback(() => {
    setReloading(true);
  }, [setReloading]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const makeFeatList = useCallback(() => {
    const feats = data_util.feats;
    // debugger;
    // let ignore_us = character.mind.feats.filter(char_feat => 
    //   char_feat.source && !char_feat.source.repeatable &&
    //   char_feat.source_id !== obj.source_id).map(char_feat => { return char_feat.source_id; });
    let use_list = feats; // .filter(o => !ignore_us.includes(o._id));
    // Unnecessary because it's part of getGoodData
    // if (!char.c_options.allow_monster_feature_sources) {
    //   use_list = use_list.filter(o => !o.monster);
    // }
    let allowed_tiers: FeatTier[] = ["Origin"];
    let non_allowed_feat_tags: string[] = ["Dragonmark","Wild Talent","Fighting Style"];
    if (char.c_options.allow_eberron) {
      non_allowed_feat_tags = non_allowed_feat_tags.filter(o => o !== "Dragonmark");
    }
    if (char.c_options.allow_wild_talent) {
      non_allowed_feat_tags = non_allowed_feat_tags.filter(o => o !== "Wild Talent");
    }
    if (char.special_feat_accesses.includes("Fighting Style")) {
      non_allowed_feat_tags = non_allowed_feat_tags.filter(o => o !== "Fighting Style");
    }
    if (obj.floating_feature instanceof FeatFeature) {
      const ff = obj.floating_feature;
      let new_feat_ids: string[] = [];
      if (ff.standard) {
        if (char.mind.character_level > 3) {
          allowed_tiers.push("General");
          if (char.mind.character_level > 18) {
            allowed_tiers.push("Epic");
          }
        }
      } else {
        allowed_tiers = [...ff.feat_tiers];
        non_allowed_feat_tags = non_allowed_feat_tags.filter(o => !ff.feat_tags.includes(o));
      }
      if (!flex_mode) {
        new_feat_ids = ff.feat_ids;
      }
      if (ff.feat_tags.length > 0) {
        non_allowed_feat_tags = non_allowed_feat_tags.filter(o => !ff.feat_tags.includes(o));
        use_list = use_list.filter(o => common_util.anyOverlaps(ff.feat_tags, o.tags));
      }
      if (!new_feat_ids.includes("Any") && new_feat_ids.length > 0) {
        use_list = use_list.filter(o => new_feat_ids.includes(o._id));
      }
    }
    if (allowed_tiers.length > 0) {
      use_list = use_list.filter(o => allowed_tiers.includes(o.feat_tier));
    }
    if (non_allowed_feat_tags.length > 0) {
      use_list = use_list.filter(o => !common_util.anyOverlaps(o.tags, non_allowed_feat_tags));
    }
    // console.log(use_list);
    setUseFeats(use_list);
  }, [char, flex_mode, obj]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      makeFeatList();
      char.subscription_holder.subscribe("Recalculated", mounted, recalculated);
    }
  }, [char, fetched, setFetched, mounted, recalculated, makeFeatList]);

  useEffect(() => {
    if (obj.true_id !== cf_id) {
      setCFId(obj.true_id);
      if (char.c_options.single_option_feat_flexibility && 
        obj.floating_feature instanceof FeatFeature && 
        obj.floating_feature.feat_ids.length === 1 && 
        obj.floating_feature.feat_ids[0] !== "Any") {
        const new_flex_mode = 
          !fos.filter(o => o.source_id !== "").map(o => 
            o.source_id).includes(obj.floating_feature.feat_ids[0]);
        setFlexMode(new_flex_mode);
      } else {
        setFlexMode(false);
      }
      setSelected(fos.filter(o => o.source_id !== "").map(o => o.source_id));
      setFetched(false);
    }
  }, [obj, cf_id]);

  useEffect(() => {
    if (changing) {
      setChanging(false);
      // debugger;
      change();
    }
    // eslint-disable-next-line
  }, [changing, setChanging]);
  
  useEffect(() => {
    if (recalc) {
      setRecalc(false);
      makeFeatList();
    }
    // eslint-disable-next-line
  }, [recalc, setRecalc]);

  const change = async () => {
    // debugger;
    const new_obj = obj; // char_util.getCF({ char, cf: obj });
    if (new_obj) {
      const new_fos = new_obj.feature_options as CharacterFeat[];
      const removed = new_fos.filter(o => !selected.includes(o.source_id));
      const added = selected.filter(o => new_fos.filter(o2 => o2.source_id === o).length === 0);
      for (const char_feat of removed) {
        if (char_feat.source) {
          // console.log(char_feat);
          for (const char_item of char.inventory.items) {
            // console.log(char_item);
            char_item.char_feature_bases = char_item.char_feature_bases.filter(o => 
              o.ancestries.filter(o2 => o2.true_id === char_feat.true_id).length === 0);
            for (const scfs of char_item.sub_char_feature_sources) {
              scfs.char_feature_bases = scfs.char_feature_bases.filter(o => 
                o.ancestries.filter(o2 => o2.true_id === char_feat.true_id).length === 0);
            }
          }
          char_util.removeCharFeatureBasesFor(char, char_feat);
          char_feat.char_feature_bases = [];
          char_feat.source_id = "";
          char_feat.source_name = "";
          char_feat.source_type = "";
          char_feat.source = null;
        }
      }
      for (const id of added) {
        // Get an empty char_feat and set the selected_feat to it
        const char_feat = new_fos.find(o => o.source_id === "");
        const selected_feat = data_util.feats.find(o => o._id === id);
        if (char_feat && selected_feat) {
          await api.loadObject(selected_feat);
          await char_feat.copy_source({ char: char, feature_source: selected_feat, parent_cf: obj });
          console.log(char_feat);
          console.log(new_obj);
          console.log(char_util.getCF({ char, cf: new_obj }));
          console.log(char_util.getCFS({ char, cf: new_obj }));
        }
      }
      char.changed();
      onChange(new_obj);
    } else {
      debugger;
    }
  }

  if (!fetched) {
    return (<LoadingBox />);
  } else {
    // console.log(use_feats);
    // console.log(use_feats.map(o => o.name));
    // console.log(fos);
    // console.log(obj);
    return (
      <Grid container spacing={0} direction="row">
        { use_feats.length === 1 && char.c_options.single_option_feat_flexibility &&
          <Grid size={12}>
            <CheckBox
              name="Flexible"
              value={flex_mode}
              onChange={(changed: boolean) => {
                setFlexMode(changed);
                setRecalc(true);
              }}
            />
          </Grid>
        }
        <Grid size={12}>
          { !recalc &&
            <CharacterListOptionsInput
              data_type="feat"
              char={char}
              choose_max={fos.length} 
              use_objects={use_feats}
              selected={selected} 
              quick_fields={["feat_tier","ability_scores","tags"]}
              onLoadedFilter={(objects: ModelBase[]) => {
                // debugger;
                const new_filter: any = { search_string: [""] };
                if (objects.length > 0 && objects[0] instanceof Feat) {
                  // Check the feat_tier of all objects, default to the highest
                  const feats = objects as Feat[];
                  const feat_tiers = common_util.unique_array(feats.map(o => o.feat_tier));
                  if (feat_tiers.includes("Epic")) {
                    new_filter.feat_tier = { "$in": ["Epic"] };
                  } else if (feat_tiers.includes("General")) {
                    new_filter.feat_tier = { "$in": ["General"] };
                  } else if (feat_tiers.includes("Origin")) {
                    new_filter.feat_tier = { "$in": ["Origin"] };
                  } 
                }
                return new_filter;
              }}
              selected_elsewhere={char.spirit.feats.filter(o => o.source && o.source.repeatable === false && fos.filter(o2 => o2.true_id === o.true_id).length === 0).map(o => o.source_id)} 
              onChange={(changed: string[]) => {
                setSelected(changed);
                setChanging(true);
              }} 
              getDisabledReasons={(obj) => {
                if (obj instanceof Feat) {
                  // if (obj.name.includes("Incarnate Ascen")) {
                  //   debugger;
                  // }
                  const reasons = char_util.fitLimits(char, obj.limitations,"feat", obj._id); // , obj.name.includes("Dual Wielder")
                  return reasons === "true" ? "" : reasons;
                }
                return "";
              }}
            />
          }
        </Grid>
      </Grid>
    );
  }
}
